<template>
  <div>
    <iq-card>
      <template #headerTitle>
        <div class="d-flex align-items-center">
          <i
            class="fa fa-arrow-left mr-3 text-primary"
            style="cursor: pointer; font-size: 20px"
            @click="$router.push('/maintenance/maintenance-transactional')"
          ></i>
          <h4 class="card-title text-primary">
            <strong>PMS (Plan Maintenance System) History</strong>
          </h4>
        </div>
      </template>
      <template #body>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div class="mb-3">
            <h4
              v-if="firstRow"
              class="align-middle mb-0 d-flex align-items-center"
            >
              <b-badge variant="success">{{ firstRow?.vehicle_name }}</b-badge>
              <strong class="text-primary ml-3">
                {{ firstRow?.equipment_name ?? '_' }}
              </strong>
            </h4>
          </div>
          <b-button variant="primary" class="ml-auto" @click="handleExport">
            <i class="fa fa-file-export mr-1"></i>
            Export
          </b-button>
        </div>
        <div class="table-responsive">
          <table
            class="table align-middle table-striped table-hover table-bordered"
          >
            <thead class="thead-light">
              <tr>
                <th>Job Name</th>
                <th>Job Type / Class</th>
                <th>Interval</th>
                <th class="text-center">Last</th>
                <th>Due</th>
                <th>Postpone</th>
                <th>Actual</th>
                <th v-if="firstRow?.triggers == 'COUNTER'">Interval Process</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody style="font-size: 12px">
              <tr v-if="isLoading">
                <td
                  :colspan="firstRow?.triggers == 'COUNTER' ? 10 : 9"
                  class="text-center py-4"
                >
                  <b-spinner label="Loading..."></b-spinner>
                </td>
              </tr>
              <template v-else>
                <tr v-for="(job, index) in maintenanceData" :key="index">
                  <!-- Job Name -->
                  <td class="align-middle">
                    <div class="text-capitalize">
                      <strong>{{ job?.job_name }}</strong>
                    </div>
                  </td>

                  <!-- Job Type / Class -->
                  <td class="p-0">
                    <div class="font-weight-bold p-2 px-3">
                      {{ job?.job_type_name }}
                    </div>
                    <div class="border-top p-2 px-3">
                      <small class="text-muted">
                        {{ job?.job_class_name }}
                      </small>
                    </div>
                  </td>

                  <!-- Interval -->
                  <td class="p-0">
                    <div class="p-2 px-3">
                      <div v-if="job.triggers == 'CALENDAR'">
                        {{ job.pms_vehicle?.calendar_interval }}
                        {{ job.pms_vehicle?.calendar_type }}
                      </div>
                      <div v-else class="font-semibold">
                        {{ formatNumber(job.pms_vehicle?.interval_hour) }} Hours
                      </div>
                    </div>
                    <div class="p-2 px-3 border-top">
                      <small class="text-muted">
                        {{ job.triggers || '-' }}
                      </small>
                    </div>
                  </td>

                  <!-- Last -->
                  <td class="p-0 text-center">
                    <div class="p-2 px-3">
                      {{
                        job.last_maintenance_date
                          ? moment(job.last_maintenance_date).format(
                              'DD/MM/YYYY'
                            )
                          : '-'
                      }}
                    </div>
                    <div
                      v-if="job.triggers == 'COUNTER'"
                      class="border-top p-2 px-3 font-semibold"
                    >
                      {{
                        job.last_maintenance_hours
                          ? formatNumber(job.last_maintenance_hours) + ' Hours'
                          : '-'
                      }}
                    </div>
                  </td>

                  <!-- Due -->
                  <td>
                    <div v-if="job.triggers == 'CALENDAR'">
                      {{
                        moment(job.possible_maintenance_date).format(
                          'DD/MM/YYYY'
                        )
                      }}
                    </div>
                    <div v-if="job.triggers == 'COUNTER'" class="font-semibold">
                      {{ formatNumber(job.possible_maintenance_hours) }} Hours
                    </div>
                  </td>

                  <!-- Postpone -->
                  <td>
                    <div
                      v-if="job?.maintenance_has_postpone?.length"
                      class="text-primary cursor-pointer"
                      style="text-decoration-line: underline"
                      @click="showPostponeModal(job)"
                    >
                      <template v-if="job.triggers == 'CALENDAR'">
                        {{
                          job.maintenance_has_postpone[0]?.extra_date
                            ? moment(
                                job.maintenance_has_postpone[0]?.extra_date
                              ).format('DD/MM/YYYY')
                            : '-'
                        }}
                      </template>
                      <template v-else>
                        {{
                          formatNumber(
                            job.maintenance_has_postpone[0]?.extra_hours
                          )
                        }}
                        Hours
                      </template>
                    </div>
                    <div v-else>-</div>
                  </td>

                  <!-- Actual -->
                  <td class="p-0">
                    <div class="p-2 px-3">
                      {{
                        job.maintenance_date
                          ? moment(job.maintenance_date).format('DD/MM/YYYY')
                          : '-'
                      }}
                    </div>
                    <div
                      v-if="job.triggers == 'COUNTER'"
                      class="border-top p-2 px-3 font-semibold"
                    >
                      {{ formatNumber(job.maintenance_running_hours) }} Hours
                    </div>
                  </td>

                  <!-- Interval Process -->
                  <td v-if="job.triggers == 'COUNTER'" class="font-semibold">
                    <template>
                      {{ formatNumber(job?.interval_process) || '0' }} Hours
                    </template>
                  </td>

                  <!-- Status -->
                  <td>
                    <span
                      class="badge"
                      :class="getStatusBadgeClass(job.status)"
                    >
                      {{ job.status }}
                    </span>
                  </td>

                  <!-- Action -->
                  <td>
                    <b-button
                      :id="`action-popover-${index}`"
                      size="sm"
                      variant="outline-primary"
                    >
                      Action
                    </b-button>
                    <b-popover
                      :target="`action-popover-${index}`"
                      placement="bottom"
                      triggers="focus"
                    >
                      <div class="list-group list-group-flush">
                        <template v-for="(item, i) in actionMenu">
                          <div
                            v-if="item.divider"
                            :key="`act-${i}-divider`"
                            class="dropdown-divider"
                          ></div>
                          <button
                            v-else
                            :key="`act-${i}-${item.key}`"
                            type="button"
                            class="list-group-item list-group-item-action"
                            :class="{
                              'text-danger': item.variant === 'danger',
                            }"
                            @click="handleAction(item.key, job, index)"
                          >
                            {{ item.label }}
                          </button>
                        </template>
                      </div>
                    </b-popover>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
          <div
            class="mt-4 d-flex align-items-center w-100 justify-content-between"
          >
            <div>
              <span class="mb-0">
                Total Rows :
                <strong>{{ filters.total }}</strong>
              </span>
            </div>
            <div class="d-flex align-items-center">
              <b-pagination
                class="mb-0 mr-3"
                @input="() => fetchData()"
                v-model="filters.page"
                :total-rows="filters.total"
                :per-page="filters.limit"
                :disabled="isLoading"
                first-number
                align="center"
              ></b-pagination>
              <b-form-select
                plain
                v-model="filters.limit"
                :options="limitOptions"
                style="width: 140px"
                :disabled="isLoading"
                @input="() => fetchData()"
              ></b-form-select>
            </div>
          </div>
        </div>
      </template>
    </iq-card>

    <CustomSidebar
      :title="sidebar.title"
      v-model="sidebar.show"
      :size="sidebar.size"
    >
      <template v-if="sidebar.title == 'Detail Maintenance'">
        <MaintenanceDetail :selectedData="sidebar.data" />
      </template>
    </CustomSidebar>

    <!-- Modal Postpone Info -->
    <b-modal
      v-model="postponeModal.show"
      title="Postpone Info"
      centered
      hide-footer
    >
      <template v-if="postponeModal.data?.length">
        <PostponeLogs
          :logs="postponeModal.data"
          :method="firstRow?.triggers || 'CALENDAR'"
        />
      </template>
    </b-modal>
  </div>
</template>
<script>
import moment from '@/node_modules/moment/moment'
import FilterMaintenanceFormNew from '../FilterMaintenanceFormNew.vue'
import PlanMaintenanceSummary from '../PlanMaintenanceSummary.vue'
import { limitOptions } from '@/src/core/mock/data/optionsData'
import { pmsActions } from '@/src/Utils/helper'
import CustomSidebar from '@/src/components/global/CustomSidebar.vue'
import MaintenanceProcessInfo from '../MaintenanceProcessInfo.vue'
import FormMaintenancePostpone from '../FormMaintenancePostpone.vue'
import MaintenanceDetail from '../MaintenanceDetail.vue'
import { debounce } from 'lodash'
import { getMiStatusBadgeClass } from '@/src/plugins/helpers'
import MaintenanceLogs from './MaintenanceLogs.vue'
import PostponeLogs from './PostponeLogs.vue'

export default {
  name: 'MaintenancehHstoryByJob',
  components: {
    PlanMaintenanceSummary,
    FilterMaintenanceFormNew,
    CustomSidebar,
    MaintenanceProcessInfo,
    FormMaintenancePostpone,
    MaintenanceDetail,
    MaintenanceLogs,
    PostponeLogs,
  },
  data() {
    return {
      modal: {
        show: false,
        data: null,
      },
      postponeModal: {
        show: false,
        data: null,
      },
      sidebar: {
        data: null,
        show: false,
        title: '',
        size: '50%',
      },
      moment,
      limitOptions,
      showModalFilter: false,
      maintenanceData: [],
      maintenanceChecked: [],
      firstRow: null,
      isLoading: false,
      actionMenu: [{ key: 'detailPms', label: 'Detail PMS' }],
      triggers: 'CALENDAR',
      filters: {
        search: '',
        status: null,
        dateRange: [
          new Date(moment().startOf('year')),
          new Date(moment().endOf('year')),
        ],
        limit: 10,
        page: 1,
        total: 1,
        vehicleIds: [],
        jobTypeIds: [],
        jobClassIds: [],
        vehicleTypeIds: [],
        filterJobType: 'jobClass',
      },
    }
  },
  methods: {
    ...pmsActions,
    getStatusBadgeClass(status) {
      return getMiStatusBadgeClass(status)
    },
    formatNumber(number) {
      // Format number with thousand separators
      if (!number && number !== 0) return '0'

      return Number(number).toLocaleString('id-ID')
    },
    showPostponeModal(job) {
      this.postponeModal.data = job.maintenance_has_postpone
      this.postponeModal.show = true
    },
    debouncedSearch: debounce(function () {
      this.filters.page = 1
      this.fetchData()
    }, 500),
    async handleExport() {
      try {
        const payload = {
          search: this.filters.search,
          status: this.filters.status,
          startDate: this.filters.dateRange
            ? moment(this.filters.dateRange[0]).format('YYYY-MM-DD')
            : null,
          endDate: this.filters.dateRange
            ? moment(this.filters.dateRange[1]).format('YYYY-MM-DD')
            : null,
          limit: this.filters.limit,
          page: this.filters.page,
          vehicleIds: this.filters.vehicleIds,
          jobTypeIds: this.filters.jobTypeIds,
          jobClassIds: this.filters.jobClassIds,
          vehicleTypeIds: this.filters.vehicleTypeIds,
          filterJobType: this.filters.filterJobType,
          pms_vehicle_id: this.$route.query.pms_vehicle_id,
        }
        const res = await this.exportPmsSummaryByJob(payload)
        saveAs(
          res.data,
          `maintenance_summary_${this.firstRow?.vehicle_name}_${this.firstRow?.equipment_name}.xlsx`
        )
        this.$swal({
          title: 'Success',
          text: res.message,
          icon: 'success',
        })
      } catch (error) {
        console.log(error)
        this.$swal({
          title: 'Failed Export Maintenance Summary',
          icon: 'error',
        })
      }
    },
    handleAction(actionKey, job, index) {
      const map = {
        detailPms: this.detailPms,
      }
      const fn = map[actionKey]
      if (typeof fn === 'function') {
        fn(job)
      }
      this.hideActionPopover(index)
    },
    hideActionPopover(index) {
      // Programmatically hide specific popover by target id
      this.$root.$emit('bv::hide::popover', `action-popover-${index}`)
    },
    toggleSelectAll(checked) {
      const currentPageIds = this.maintenanceData.map((item) => item.id)
      if (checked) {
        const unique = new Set([...this.maintenanceChecked, ...currentPageIds])
        this.maintenanceChecked = Array.from(unique)
      } else {
        this.maintenanceChecked = this.maintenanceChecked.filter(
          (id) => !currentPageIds.includes(id)
        )
      }
    },
    async fetchData() {
      // Fetch maintenance data based on filters
      try {
        this.isLoading = true
        const payload = {
          search: this.filters.search,
          status: this.filters.status,
          startDate: this.filters.dateRange
            ? moment(this.filters.dateRange[0]).format('YYYY-MM-DD')
            : null,
          endDate: this.filters.dateRange
            ? moment(this.filters.dateRange[1]).format('YYYY-MM-DD')
            : null,
          limit: this.filters.limit,
          page: this.filters.page,
          vehicleIds: this.filters.vehicleIds,
          jobTypeIds: this.filters.jobTypeIds,
          jobClassIds: this.filters.jobClassIds,
          vehicleTypeIds: this.filters.vehicleTypeIds,
          filterJobType: this.filters.filterJobType,
          pms_vehicle_id: this.$route.query.pms_vehicle_id,
        }

        const res = await this.getPmsSummaryByJob(payload)

        if (res.status == 'success') {
          const { data, meta } = res.data.data
          const mappingData = data.map((item) => ({
            ...item,
            job_class_name:
              item?.pms_vehicle?.maintenance_job_class?.job_class ?? '',
            job_type_name:
              item?.pms_vehicle?.maintenance_job_type?.description ?? '',
            equipment_name:
              item?.pms_vehicle?.maintenance_master.equipment_name ?? '',
            equipment_id:
              item?.pms_vehicle?.maintenance_master.equipment_id ?? '',
            attachments: item?.maintenance_attachments ?? [],
            logs: item?.maintenance_has_log ?? [],
            comments: item?.maintenance_has_comment ?? [],
            vehicle_name: item?.vehicle?.name ?? '',
            vehicle_type_name: item?.vehicle?.vehicle_type?.name ?? '',
            vehicle_flag: item?.vehicle?.flag ?? '',
            company_name: item?.vehicle?.company?.company ?? '',
            vehicle_code: item?.vehicle?.code ?? '',
            year_build_country: item?.vehicle?.year_build_country ?? '',
          }))

          this.maintenanceData = mappingData
          if (mappingData?.length) {
            this.firstRow = mappingData[0] || null
          }

          console.log('data', meta)

          this.filters.page = meta?.page
          this.filters.limit = meta?.per_page
          this.filters.total = meta?.total_item
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    detailPms(job) {
      this.sidebar.title = 'Detail Maintenance'
      this.sidebar.data = job
      this.sidebar.show = true
      this.sidebar.size = '50%'
    },
  },
  computed: {
    isCheckedAll() {
      return this.maintenanceData.every((el) =>
        this.maintenanceChecked.some((id) => id == el.id)
      )
    },
  },
  watch: {
    'filters.search'() {
      this.debouncedSearch()
    },
  },
  mounted() {
    this.fetchData()
  },
}
</script>
<style scoped>
table td {
  line-height: 18px !important;
}

.vertical-line {
  border-left: 2.4px solid #e5e7eb;
  min-height: 80px;
  position: relative;
}
.circle {
  width: max-content;
  border: 1.5px solid #1f48aa;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  padding: 4px;
  position: absolute;
  left: -10px;
  top: 4px;
}
.dot {
  width: 8px;
  height: 8px;
  background: #1f48aa;
  border-radius: 50%;
}
.text-blue-400 {
  color: #334697;
}
.font-semibold {
  font-weight: 600;
}
</style>
